import { SlideshowSelectEvent } from '@theme/events';

/**
 * Timeline slider component
 * Handles navigation between timeline slides via label buttons.
 */
class TimelineSlider extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {number} */
  #touchStartScrollLeft = 0;

  connectedCallback() {
    this.#buildNav();
    this.addEventListener('click', this.#handleClick);

    requestAnimationFrame(() => {
      const scroller = this.#scroller;
      if (scroller) {
        scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
        scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
        scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      }
    });

    this.#resizeObserver = new ResizeObserver(() => {
      this.#snapToCurrentSlide();
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    const scroller = this.#scroller;
    if (scroller) {
      scroller.removeEventListener('scroll', this.#handleScroll);
      scroller.removeEventListener('touchstart', this.#handleTouchStart);
      scroller.removeEventListener('touchend', this.#handleTouchEnd);
    }
    this.#resizeObserver?.disconnect();
  }

  get #scroller() {
    return /** @type {HTMLElement | null} */ (this.querySelector('slideshow-slides'));
  }

  get #slides() {
    const scroller = this.#scroller;
    if (!scroller) return [];
    return /** @type {HTMLElement[]} */ ([...scroller.children].filter(
      (el) => el instanceof HTMLElement && el.classList.contains('timeline-slide')
    ));
  }

  get #navItems() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-slide-index]')]);
  }

  #buildNav() {
    const nav = this.querySelector('.timeline-nav');
    if (!nav) return;

    const slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.timeline-slide')]);

    slides.forEach((slide, index) => {
      const label = slide.dataset.label ?? '';
      const button = document.createElement('button');
      button.className = 'timeline-nav__item';
      button.type = 'button';
      button.dataset.slideIndex = String(index);
      button.setAttribute('aria-selected', String(index === 0));
      button.textContent = label;
      nav.appendChild(button);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target)?.closest('[data-slide-index]');
    if (!(button instanceof HTMLElement)) return;

    const index = parseInt(button.dataset.slideIndex ?? '0', 10);
    this.#goToSlide(index);
  };

  /**
   * @param {number} index
   */
  #goToSlide(index) {
    const scroller = this.#scroller;
    const slides = this.#slides;
    const target = slides[index];
    if (!scroller || !target) return;

    scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    this.#updateNav(index);
  }

  #handleScroll = () => {
    this.#updateNav(this.#getActiveIndex());
  };

  #handleTouchStart = (/** @type {TouchEvent} */ event) => {
    const scroller = this.#scroller;
    if (!scroller) return;

    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartScrollLeft = scroller.scrollLeft;
  };

  #handleTouchEnd = (/** @type {TouchEvent} */ event) => {
    const slides = this.#slides;
    if (slides.length === 0) return;

    const deltaX = this.#touchStartX - event.changedTouches[0].clientX;
    const threshold = 30;

    if (Math.abs(deltaX) < threshold) return;

    const currentIndex = this.#getActiveIndex();
    const targetIndex = deltaX > 0
      ? Math.min(currentIndex + 1, slides.length - 1)
      : Math.max(currentIndex - 1, 0);

    this.#goToSlide(targetIndex);
  };

  /**
   * Returns the index of the slide closest to the current scroll position.
   * @returns {number}
   */
  #getActiveIndex() {
    const scroller = this.#scroller;
    const slides = this.#slides;
    if (!scroller || slides.length === 0) return 0;

    const scrollLeft = scroller.scrollLeft;
    let activeIndex = 0;
    let minDistance = Infinity;

    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = i;
      }
    });

    return activeIndex;
  }

  #snapToCurrentSlide() {
    const scroller = this.#scroller;
    const slides = this.#slides;
    const target = slides[this.#getActiveIndex()];
    if (!scroller || !target) return;

    scroller.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
  }

  /**
   * @param {number} activeIndex
   */
  #updateNav(activeIndex) {
    for (const [i, item] of this.#navItems.entries()) {
      item.setAttribute('aria-selected', String(i === activeIndex));
    }
  }
}

if (!customElements.get('timeline-slider')) {
  customElements.define('timeline-slider', TimelineSlider);
}
